import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import type {
  MaybeClaimInviteTaskMutation,
  MaybeClaimInviteTaskMutationVariables,
  useMaybeClaimInviteTask_currentUser,
} from './__generated__/useMaybeClaimInviteTask.graphql.ts';

const MAYBE_CLAIM_INVITE_TASK_MUTATION = gql`
  mutation MaybeClaimInviteTaskMutation($taskId: ID!, $sport: Sport!) {
    claimTask(input: { taskId: $taskId }) {
      currentUser {
        slug
        inviteTaskAsReferee(sport: $sport) {
          id
          aasmState
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  MaybeClaimInviteTaskMutation,
  MaybeClaimInviteTaskMutationVariables
>;

export const useMaybeClaimInviteTask = () => {
  const { sport } = useSportContext();
  const [claimTask] = useMutation(MAYBE_CLAIM_INVITE_TASK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  return async (currentUser: Nullable<useMaybeClaimInviteTask_currentUser>) => {
    const inviteTask = currentUser?.inviteTaskAsReferee;
    if (
      !inviteTask ||
      inviteTask.aasmState !== ManagerProgressionTaskState.COMPLETED
    ) {
      return false;
    }
    await claimTask({ variables: { taskId: inviteTask.id, sport } });
    return true;
  };
};

useMaybeClaimInviteTask.fragments = {
  currentUser: gql`
    fragment useMaybeClaimInviteTask_currentUser on CurrentUser {
      slug
      inviteTaskAsReferee(sport: $sport) {
        id
        aasmState
      }
    }
  ` as TypedDocumentNode<useMaybeClaimInviteTask_currentUser>,
};
